import { Chart as OgChart } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, RouterProps } from 'react-router'
import { useInfoBox } from '../../components/infoBox/useInfoBox'
import ModeSwitch from '../../components/ModeSwitch'
import useUser from '../../contexts/hooks/useUser'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../contexts/UserContext'
import usePreference from '../../utils/preferences/usePreference'
import PageWrapper from '../../wrappers/pageWrapper/PageWrapper'
import ConsumptionChart from './charts/consumptionChart/ConsumptionChart'
import ProductionChart from './charts/productionChart/ProductionChart'
import './styles/Electricity.scss'
import getInfoBoxContent from './utils/getInfoBoxContent'
import DayAheadPricesTab from './tabs/DayAheadPricesTab'

OgChart.register(annotationPlugin)

export type ElectricityPageElement = 'price' | 'consumption' | 'production'

interface ElectricityPageParams extends RouterProps {
	element: ElectricityPageElement
	time: 'day' | 'week' | 'month' | 'year'
}

const Electricity: React.FC<RouteComponentProps> = ({ match }) => {
	const { isAnonymousUser } = useContext(UserContext)
	const object = useContext(ObjectContext)

	const { user } = useUser()
	const [storedMode, setPref] = usePreference('electricityMode', 'consumption')

	const [mode, setMode] = useState<ElectricityPageElement>(() => {
		const params = match.params as ElectricityPageParams
		if (params.element) return params.element
		return 'consumption'
	})

	useEffect(() => {
		const loadStorageMode = async () => {
			if (storedMode) {
				setMode(storedMode as ElectricityPageElement)
			}
		}
		loadStorageMode()
	}, [])

	const { open: openInfoBox, close: closeInfoBox, InfoBoxComponent } = useInfoBox<HTMLDivElement>()
	const anchorRef = useRef<HTMLDivElement | null>(null)

	const { t } = useTranslation('pages_electricity')

	const modeOptions: { mode: ElectricityPageElement; label: string; enabled: boolean }[] = [
		{
			mode: 'consumption' as ElectricityPageElement,
			label: t('chartModeSwitch.consumption'),
			enabled: true,
		},
		{
			mode: 'production' as ElectricityPageElement,
			label: t('chartModeSwitch.production'),
			enabled:
				isAnonymousUser ||
				!!object.synerallProductionId ||
				!!object.productionGsrn ||
				!!user?.panelLinked,
		},

		{
			mode: 'price' as ElectricityPageElement,
			label: t('chartModeSwitch.prices'),
			enabled: true,
		},
	].filter(x => x.enabled)

	const handleModeChange = (newMode: ElectricityPageElement) => {
		setMode(newMode)
		closeInfoBox()
	}

	useEffect(() => {
		const params = match.params as ElectricityPageParams
		if (params.element) {
			setMode(params.element)
		}
	}, [match])

	useEffect(() => {
		if (!storedMode) return
		setPref(mode)
	}, [mode])

	const handleInfoClick = () => {
		try {
			const content = getInfoBoxContent(mode, t)

			const baseConfig = {
				content,
				anchor: anchorRef,
			}

			if (mode === 'production') {
				openInfoBox([baseConfig])
			} else if (mode === 'price') {
				openInfoBox([
					{
						...baseConfig,
						props: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } },
					},
				])
			} else if (mode === 'consumption') {
				openInfoBox([baseConfig])
			}
		} catch (error) {
			console.error('Error opening info box:', error)
		}
	}
	return (
		<PageWrapper page='electricity' appBarOptions={{ onInfoClick: handleInfoClick }}>
			<div className='flex flex-col gap-md'>
				<ModeSwitch<ElectricityPageElement>
					customBackground='!m-0'
					currentMode={mode}
					options={modeOptions}
					onModeChange={handleModeChange}
					shadow
				/>
				<div>
					{mode === 'production' && (
						<ProductionChart
							anchorRef={anchorRef}
							timespan={(match.params as ElectricityPageParams).time}
						/>
					)}
					{mode === 'price' && (
						<DayAheadPricesTab anchorRef={anchorRef} />
					)}
					{mode === 'consumption' && <ConsumptionChart anchorRef={anchorRef} />}
				</div>
			</div>

			{InfoBoxComponent}
		</PageWrapper>
	)
}

export default Electricity
